<h1 mat-dialog-title>Pending Consent Detected</h1>
<div mat-dialog-content class="dialog-content">
    <ng-container *ngIf="!signNow">
        <p>
            This patient has not yet provided their consent for the following:
        </p>
        <ng-container *ngIf="onboardingConsent?.length">
            <p>Onboarding Consent:</p>
            <ul>
                <li *ngFor="let consent of onboardingConsent">
                    {{ consent.label }}
                </li>
            </ul>
        </ng-container>
        <ng-container *ngIf="procedureConsent?.length">
            <p>Procedure Consent:</p>
            <ul>
                <li *ngFor="let consent of procedureConsent">
                    {{ consent.label }}
                </li>
            </ul>
        </ng-container>
    </ng-container>

    <ng-container *ngIf="consentFormList.length && signNow">
        <div class="consent-form">
            <form [formGroup]="consentForm" *ngFor="let consentForm of consentFormGroups; let i = index">
                <div class="consent-heading">
                    <h3>{{ consentForm.get('consentLabel')?.value }}</h3>
                    <div>
                        <button mat-icon-button matTooltip="View fullscreen consent"
                            (click)="viewConsentFullscreen(consentForm)">
                            <mat-icon>fullscreen</mat-icon>
                        </button>
                        <button mat-icon-button matTooltip="Download PDF consent"
                            (click)="downloadConsentPDF(consentForm)">
                            <mat-icon>download</mat-icon>
                        </button>
                    </div>
                </div>

                <pre [innerHtml]="consentForm.get('consentText')?.value"
                    class="preserve-line-breaks consent-text"></pre>

                <ng-container
                    *ngIf="consentForm.get('confirmSide')?.value && consentForm.get('sideOptions')?.value?.length">
                    <mat-form-field appearance="outline" class="m-t-20 full-width">
                        <mat-label>Procedure Side</mat-label>
                        <mat-select formControlName="selectedProcedureSide" required>
                            <mat-option *ngFor="let option of consentForm.get('sideOptions')?.value" [value]="option">
                                {{ option }}
                            </mat-option>
                        </mat-select>
                    </mat-form-field>
                </ng-container>


                <ng-container
                    *ngIf="consentForm.get('confirmImplant')?.value && consentForm.get('implantOptions')?.value?.length">
                    <mat-form-field appearance="outline" class="m-t-20 full-width">
                        <mat-label>Implant</mat-label>
                        <mat-select formControlName="selectedImplant" required>
                            <mat-option *ngFor="let option of consentForm.get('implantOptions')?.value"
                                [value]="option">
                                {{ option }}
                            </mat-option>
                        </mat-select>
                    </mat-form-field>
                </ng-container>


                <ng-container
                    *ngIf="consentForm.get('confirmTransfusion')?.value && consentForm.get('transfusionOptions')?.value?.length">
                    <mat-form-field appearance="outline" class="m-t-20 full-width">
                        <mat-label>Blood Transfusion</mat-label>
                        <mat-select formControlName="selectedTransfusion" required>
                            <mat-option *ngFor="let option of consentForm.get('transfusionOptions')?.value"
                                [value]="option">
                                {{ option }}
                            </mat-option>
                        </mat-select>
                    </mat-form-field>
                </ng-container>

                <ng-container *ngIf="consentForm.get('additionalRequiredConsent')?.value?.length">
                    <h3>REQUIRED CONSENT</h3>
                    <p class="align-left"><strong>I Consent to the following:</strong></p>
                    <mat-radio-group formControlName="selectedRequiredConsent">
                        <mat-radio-button *ngFor="let consent of consentForm.get('additionalRequiredConsent')?.value"
                            [value]="consent">
                            {{ consent }}
                        </mat-radio-button>
                    </mat-radio-group>
                </ng-container>

                <ng-container *ngIf="consentForm.get('conditionalConsent')?.value?.length">
                    <h3>OPTIONAL CONSENT</h3>
                    <p class="align-left"><strong>I Consent to the following:</strong></p>
                    <div formArrayName="conditionalConsent">
                        <div *ngFor="let conditional of getConditionalConsentArray(consentForm).controls; let i = index"
                            [formGroupName]="i">
                            <mat-checkbox formControlName="consentGiven">{{
                                conditional.get('name')?.value }}</mat-checkbox>
                        </div>
                    </div>
                </ng-container>

                <ng-container *ngIf="consentForm.get('form')?.value.consentType === 'interactive'">
                    <p class="p-t-10 align-left">Based on the information and statements provided, I
                        acknowledge that my doctor has thoroughly explained the procedure to
                        me, answered all my questions, and I hereby request and consent to the
                        treatment.</p>
                </ng-container>

                <div class="consent-checkbox">
                    <mat-checkbox formControlName="consentGiven">
                        I have read and agree to the terms
                    </mat-checkbox>
                </div>

            </form>
        </div>

    </ng-container>

    <!--
      §14.3 - the shared attachment picker, pre-ticked from `sendWithConsentRequest`.

      Only on the SEND path: a consent signed at the desk is not an email, so there is nothing for a
      document to ride along with. It sits beside the consent forms' own information sheets rather
      than replacing them - §14.6 decision 2 retires those into this picker later, and the two
      coexist until it does.

      The heading is the picker's own `label` now: since wave 1 the picker HIDES ITSELF for a
      practice that has no documents at all, and a heading written out here would have survived it
      and left "DOCUMENTS TO SEND WITH THIS REQUEST" standing over nothing.
    -->
    <div class="consent-documents" *ngIf="!signNow && !stateMode" data-test="consent-document-picker">
        <app-document-attachment-picker context="consentRequest" [patientId]="patientId"
            [groupId]="patientGroupId" [selection]="selectedDocumentIds"
            label="Documents to send with this request"
            (selectionChange)="onDocumentSelectionChange($event)">
        </app-document-attachment-picker>
    </div>

    <!--
      AND SAY SO ON THE OTHER PATH. `signNow` is a consent signed at the desk: it is not emailed, so
      nothing can ride along with it. That reasoning was already sound and is unchanged - but a
      block that simply vanished left the user to guess why, which is the same complaint the owner
      raised about the picker itself.
    -->
    <p class="consent-documents-signing" *ngIf="signNow && !stateMode" data-test="consent-documents-signing">
        This consent is being signed here, so it is not emailed and no practice documents can be
        sent with it. Use Send Now instead to email the request with documents attached.
    </p>

    <div class="signature-pad-container">
        <div class="signature-pad" *ngIf="signNow">
            <app-signature-pad [emitOnDragEnd]="true" format="base64" (done)="getSignatureData($event)"
                (cleared)="getSignatureData('')" [signatoryRequired]="true">
            </app-signature-pad>
        </div>

        <form [formGroup]="form">
            <mat-form-field appearance="outline" class="m-t-5 m-r-5" *ngIf="!signNow && !stateMode">
                <mat-label>Email</mat-label>
                <input matInput formControlName="email">
            </mat-form-field>

            <mat-form-field appearance="outline">
                <mat-label>Requesting Physician</mat-label>
                <mat-select formControlName="physicianId">
                    <mat-option *ngFor="let user of groupUsers" [value]="user.userId">
                        {{user.title}} {{user.name}} {{user.surname}}</mat-option>
                </mat-select>
            </mat-form-field>

        </form>

        <ng-container *ngIf="lastSent">
            <span class="status-message orange"><mat-icon>check_circle</mat-icon> Sent: {{lastSent.createdAt | date
                :
                'dd/MM/yyyy HH:mm'}}</span>
        </ng-container>
        <p class="resend-cooldown-message" *ngIf="!signNow && !stateMode && getResendCooldownMessage()">{{ getResendCooldownMessage() }}</p>
    </div>
</div>
<div mat-dialog-actions>
    <button mat-button (click)="closeDialog('')">Ignore</button>
    <button mat-button (click)="onSignNow()" *ngIf="!signNow">Sign Now</button>
    <button mat-button (click)="closeDialog('new-consent')" *ngIf="signNow">Save</button>
    <button mat-button [disabled]="!form.controls['email'].valid || !getResendCooldownState().canResend" (click)="closeDialog('email-consent')"
        *ngIf="!signNow && !stateMode">Send Now</button>
</div>

<ng-template #fullScreenDialog>
    <h1 mat-dialog-title>{{fullScreenConsentForm.consentLabel}}</h1>
    <div mat-dialog-content>
        <pre [innerHtml]="fullScreenConsentForm.consentText"
            class="preserve-line-breaks consent-text no-height-limit"></pre>
        <ng-container *ngIf="fullScreenConsentForm.form.consentType === 'interactive'">
            <p class="p-t-10 align-left">Based on the information and statements provided, I
                acknowledge that my doctor has thoroughly explained the procedure to
                me, answered all my questions, and I hereby request and consent to the
                treatment.</p>
        </ng-container>
    </div>
    <div mat-dialog-actions class="dialog-actions">
        <button mat-raised-button color="primary" mat-dialog-close>Close</button>
    </div>

</ng-template>